import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Field, NativeSelect, LoadingBlock, Spinner, Logo } from "@/components/Shared";

const ZONES = ["America/Chicago", "America/New_York", "America/Denver", "America/Phoenix", "America/Los_Angeles", "America/Anchorage", "Pacific/Honolulu"];

const Toggle = ({ label, hint, checked, onChange, testId }) => (
  <div className="flex items-start justify-between gap-4 py-2">
    <div><p className="text-sm font-semibold">{label}</p>{hint && <p className="text-xs text-slate-600">{hint}</p>}</div>
    <Switch checked={!!checked} onCheckedChange={onChange} aria-label={label} data-testid={testId} />
  </div>
);

export default function Settings() {
  const { refreshMe } = useAuth();
  const [s, setS] = useState(null);
  const [emails, setEmails] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/settings").then((r) => { setS(r.data); setEmails((r.data.recipient_emails || []).join("\n")); }); }, []);
  if (!s) return <LoadingBlock />;
  const set = (k, v) => setS({ ...s, [k]: v });

  const save = async () => {
    setBusy(true);
    try {
      const body = { ...s, recipient_emails: emails.split(/[\n,;]+/).map((x) => x.trim()).filter(Boolean),
        max_photo_mb: Number(s.max_photo_mb), max_photos_per_report: Number(s.max_photos_per_report) };
      delete body.last_daily_sent_date; delete body.updated_at; delete body.updated_by;
      const { data } = await api.put("/settings", body);
      setS(data);
      setEmails((data.recipient_emails || []).join("\n"));
      refreshMe();
      toast.success("Settings saved");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <div className="fade-up pb-10">
      <PageHeader eyebrow="Configuration" title="Settings" actions={<button className="btn-brand h-11" onClick={save} disabled={busy} data-testid="settings-save-button">{busy && <Spinner className="text-white" />}Save settings</button>} />
      <div className="grid gap-5 lg:grid-cols-2">
        <section className="panel space-y-4 p-5">
          <p className="font-display text-xl font-semibold">Company</p>
          <div className="flex items-center gap-4 rounded-md border border-dashed border-border p-3"><Logo className="h-14" /><p className="text-xs text-slate-500">Logo used in the portal, PDF and Excel exports.</p></div>
          <Field label="Company name"><input className="field-input" value={s.company_name} onChange={(e) => set("company_name", e.target.value)} data-testid="settings-company-input" /></Field>
          <Field label="Business timezone" hint="Report dates and 'today' follow this timezone">
            <NativeSelect value={s.timezone} onChange={(e) => set("timezone", e.target.value)} data-testid="settings-timezone-select">
              {ZONES.map((z) => <option key={z} value={z}>{z}</option>)}
            </NativeSelect>
          </Field>
          <Field label="Signature suffix" hint='Printed after the inspector name, e.g. "QDB Consulting LLC."'><input className="field-input" value={s.signature_suffix} onChange={(e) => set("signature_suffix", e.target.value)} data-testid="settings-signature-input" /></Field>
          <Field label="Report footer tagline"><textarea rows={2} className="field-area text-sm" value={s.tagline} onChange={(e) => set("tagline", e.target.value)} data-testid="settings-tagline-input" /></Field>
          <Field label="Default safety text"><textarea rows={4} className="field-area text-sm" value={s.default_safety} onChange={(e) => set("default_safety", e.target.value)} data-testid="settings-safety-input" /></Field>
        </section>
        <div className="space-y-5">
          <section className="panel p-5" data-testid="settings-daily-delivery">
            <p className="font-display text-xl font-semibold">Daily report delivery</p>
            <div className="divide-y divide-border">
              <Toggle label="Send automatically every day" hint="Emails all reports submitted that day after the time below" checked={s.daily_enabled} onChange={(v) => set("daily_enabled", v)} testId="settings-daily-enabled" />
              <div className="grid grid-cols-2 gap-3 py-3">
                <Field label="Time of day"><input type="time" className="field-input" value={s.daily_time} onChange={(e) => set("daily_time", e.target.value)} data-testid="settings-daily-time" /></Field>
                <Field label="Timezone"><input className="field-input" value={s.timezone} disabled /></Field>
              </div>
              <div className="py-3">
                <Field label="Recipient emails" hint="One per line. Leave empty until the owner's addresses are confirmed.">
                  <textarea rows={3} className="field-area text-sm" placeholder="owner@example.com" value={emails} onChange={(e) => setEmails(e.target.value)} data-testid="settings-recipients-input" />
                </Field>
              </div>
              <Toggle label="Attach PDF daily report" checked={s.include_pdf} onChange={(v) => set("include_pdf", v)} testId="settings-include-pdf" />
              <Toggle label="Include report summary" checked={s.include_summary} onChange={(v) => set("include_summary", v)} testId="settings-include-summary" />
              <Toggle label="Include links to reports & photos" checked={s.include_photo_links} onChange={(v) => set("include_photo_links", v)} testId="settings-include-links" />
            </div>
          </section>
          <section className="panel p-5">
            <p className="font-display text-xl font-semibold">Reports & photos</p>
            <Toggle label="Employees can edit submitted reports the same day" hint="Until midnight in the business timezone. Admins can always edit." checked={s.employee_edit_same_day} onChange={(v) => set("employee_edit_same_day", v)} testId="settings-employee-edit" />
            <div className="mt-3 grid grid-cols-2 gap-3">
              <Field label="Max photo size (MB)"><input type="number" min="1" max="50" className="field-input" value={s.max_photo_mb} onChange={(e) => set("max_photo_mb", e.target.value)} data-testid="settings-max-photo-mb" /></Field>
              <Field label="Max photos per report"><input type="number" min="1" max="200" className="field-input" value={s.max_photos_per_report} onChange={(e) => set("max_photos_per_report", e.target.value)} data-testid="settings-max-photos" /></Field>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
